import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { formValueSelector } from 'redux-form';
import usePurchase from 'hooks/store/usePurchase';
import usePayment from 'hooks/store/usePayment';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import { selectCardCheckoutDecision } from 'selectors/cardCheckoutSelectors';

const cardSelector = formValueSelector('card');
const purchaserSelector = formValueSelector('purchaser');

const purchaserFields = [
  'purchaserFirstName',
  'purchaserLastName',
  'purchaserSecondLastName',
  'email',
  'phone',
  'phoneCountry',
  'phoneCode',
  'documentType',
  'documentId',
  // Keep the invoice request and withdrawal marker in payment saves and seat-edit snapshots.
  'taxpayerId',
  'taxpayerLegalName',
  'taxpayerAddress',
  'invoice',
];

/**
 * Select the writable Quick Purchase buyer fields for state and current payment saves.
 * @param {object} state - Redux state.
 * @returns {object} Current buyer identity and optional choices.
 */
export const selectQuickCheckoutPurchaserFormValues = (state) =>
  purchaserSelector(state, ...purchaserFields, 'promotionalTerms', 'cdsFrequentPassengerOptIn');

/**
 * Keep persisted buyer details when only agreements are editable at checkout.
 * @param {object} state - Current Redux state.
 * @param {boolean} useStoredPurchaser - Whether the purchaser form is absent.
 * @returns {object} Canonical save fields with the current optional choices.
 */
export const selectQuickCheckoutPurchaserSaveValues = (state, useStoredPurchaser = false) => {
  const values = selectQuickCheckoutPurchaserFormValues(state) || {};
  if (!useStoredPurchaser) return values;

  const { purchase } = state;
  /**
   * Read a persisted buyer field.
   * @param {string} key - Buyer field name.
   * @returns {*} Persisted value from an Immutable or plain purchase.
   */
  const readField = (key) => (purchase?.get ? purchase.get(key) : purchase?.[key]);
  const storedPurchaser = {};
  purchaserFields.forEach((key) => {
    const value = readField(key);
    if (value !== undefined) storedPurchaser[key] = value;
  });
  const contact = {
    phone: readField('phone'),
    phoneCountry: readField('phoneCountry'),
    phoneCode: readField('phoneCode'),
  };
  // Never combine a persisted phone with an old country or code from storage.
  if (Object.values(contact).some((value) => value !== undefined)) {
    Object.assign(storedPurchaser, contact);
  }
  return { ...values, ...storedPurchaser };
};

/**
 * useQuickCheckoutState hook.
 * Compose existing store hooks and derive QuickCheckout-specific state.
 * Replaces mapStateToProps from QuickCheckoutContainer.
 * @returns {object} QuickCheckout state.
 */
function useQuickCheckoutState() {
  const purchase = usePurchase();
  const payment = usePayment();
  const env = useWhitelabelEnvs();
  const features = useWhitelabelFeatures();

  const isUpdating = useSelector((state) => state.purchase.get('isUpdating'));
  const paymentEngine = useSelector((state) => state.purchase.get('paymentEngine'));
  const selectedPaymentMethod = useSelector((state) => state.purchase.get('selectedPaymentMethod'));
  const cardCheckoutMode = useSelector(
    (state) => selectCardCheckoutDecision(state, selectedPaymentMethod).mode,
  );

  const cardValues = useSelector((state) => {
    return cardSelector(
      state,
      'holderName',
      'cardNumber',
      'cvv2',
      'expirationMonth',
      'expirationYear',
      'expirationDate',
      'paymentPlan',
      'monthlySelectedPlan',
      'billingAddress',
      'cardBrand',
    );
  });

  const purchaserFormValues = useSelector(selectQuickCheckoutPurchaserFormValues);

  // Legal is validation-only; it must never enter the canonical purchase writer.
  const legalTerms = useSelector((state) => purchaserSelector(state, 'legalTerms'));

  const fetchingPayment = payment.isFetching || false;
  const paymentId = payment.id || null;
  const cardErrorOccured = payment.cardErrorOccured || false;
  const insuranceConfig = purchase.config || {};

  const carbonOffset = purchase.config?.carbonOffset;
  const wantsCarbonOffset = Boolean(purchase.wantsCarbonOffset);
  const updatingCarbonOffset = Boolean(purchase.updatingCarbonOffset);

  const purchaser = useMemo(() => {
    if (purchaserFormValues?.purchaserFirstName) {
      const fields = { ...purchaserFormValues };
      if (fields.phone) fields.phone = fields.phone.replace(/ /g, '');
      return fields;
    }
    const firstPassenger = purchase.passengers?.[0] || {};
    return {
      purchaserFirstName: firstPassenger.firstName,
      purchaserLastName: firstPassenger.lastName,
      email: firstPassenger.email,
      phone: firstPassenger.phone,
      phoneCode: firstPassenger.phoneCode,
      documentId: firstPassenger.documentId,
      documentType: firstPassenger.documentType,
      promotionalTerms: purchaserFormValues?.promotionalTerms,
      cdsFrequentPassengerOptIn: purchaserFormValues?.cdsFrequentPassengerOptIn,
    };
  }, [purchaserFormValues, purchase.passengers]);

  return {
    purchase,
    payment,
    env,
    features,
    isUpdating,
    fetchingPayment,
    paymentId,
    cardErrorOccured,
    cardValues,
    cardCheckoutMode,
    paymentEngine,
    selectedPaymentMethod,
    purchaser,
    purchaserFormValues,
    legalTerms,
    insuranceMessage: insuranceConfig.insuranceMessage,
    insuranceAmount: insuranceConfig.insuranceAmount,
    insuranceAmountWithTaxes: insuranceConfig.insuranceAmountWithTaxes,
    insuranceAmountIsUnitPrice: insuranceConfig.unitAmount,
    carbonOffset,
    wantsCarbonOffset,
    updatingCarbonOffset,
  };
}

export default useQuickCheckoutState;
